import { useRef, useState } from "react";
import { useAdBanners, type AdBanner, type CarouselItem } from "@/hooks/useAdBanners";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useDesignMode } from "@/hooks/useDesignMode";
import { supabase } from "@/integrations/supabase/client";
import { Star, Heart, Share2, Quote, ChevronRight, BadgeCheck, Move, Save } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface AdBannerSlotProps {
  pageKey: string;
  index?: number;
  className?: string;
}

const getPositionStyles = (banner: AdBanner): { wrapper: string; inner: React.CSSProperties; widthClass: string } => {
  const v = banner.position_v || "middle";
  const h = banner.position_h || "center";
  const o = banner.orientation || "horizontal";
  const rot = banner.rotation || 0;

  const justify = v === "top" ? "justify-start" : v === "bottom" ? "justify-end" : "justify-center";
  const align = h === "left" ? "items-start" : h === "right" ? "items-end" : "items-center";

  let baseRotate = 0;
  if (o === "diagonal") baseRotate = -8;
  else if (o === "vertical") baseRotate = 90;
  const totalRot = baseRotate + rot;

  const widthClass = o === "vertical" ? "max-w-xs" : "w-full max-w-3xl";

  return {
    wrapper: `flex w-full ${justify} ${align}`,
    inner: totalRot ? { transform: `rotate(${totalRot}deg)`, transformOrigin: "center" } : {},
    widthClass,
  };
};

/** Free positioning wrapper — drag & drop in design mode */
const FreeWrap = ({ banner, children }: { banner: AdBanner; children: React.ReactNode }) => {
  const { isDesignMode, isAdmin } = useDesignMode();
  const [pos, setPos] = useState({ x: banner.free_x, y: banner.free_y, w: banner.free_width });
  const [dirty, setDirty] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number; baseW: number; mode: "move" | "resize" } | null>(null);

  const onMouseDown = (e: React.MouseEvent, mode: "move" | "resize") => {
    if (!isDesignMode || !isAdmin) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y, baseW: pos.w, mode };
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current; if (!d) return;
      if (d.mode === "move") {
        const dx = ((ev.clientX - d.startX) / window.innerWidth) * 100;
        const dy = ((ev.clientY - d.startY) / window.innerHeight) * 100;
        setPos((p) => ({ ...p, x: Math.max(0, Math.min(100, d.baseX + dx)), y: Math.max(0, Math.min(100, d.baseY + dy)) }));
      } else {
        setPos((p) => ({ ...p, w: Math.max(120, Math.min(1600, d.baseW + (ev.clientX - d.startX))) }));
      }
      setDirty(true);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const save = async () => {
    const { error } = await supabase
      .from("ad_banners")
      .update({ free_x: pos.x, free_y: pos.y, free_width: pos.w })
      .eq("id", banner.id);
    if (error) toast({ title: "Erreur", description: error.message, variant: "destructive" });
    else { toast({ title: "📍 Position enregistrée" }); setDirty(false); }
  };

  return (
    <div
      className={isDesignMode ? "ring-2 ring-primary/60 rounded-2xl" : ""}
      style={{
        position: "fixed",
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        width: `${pos.w}px`,
        transform: "translate(-50%, -50%)",
        zIndex: banner.free_z_index || 40,
        cursor: isDesignMode ? "move" : "default",
      }}
      onMouseDown={(e) => onMouseDown(e, "move")}
    >
      {children}
      {isDesignMode && isAdmin && (
        <>
          <div
            className="absolute -bottom-2 -right-2 w-5 h-5 bg-primary rounded-full border-2 border-white shadow cursor-se-resize z-10"
            onMouseDown={(e) => onMouseDown(e, "resize")}
            title="Redimensionner"
          />
          <div className="absolute -top-9 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-card border rounded-full px-2 py-1 shadow text-xs whitespace-nowrap z-10">
            <Move className="w-3 h-3 text-primary" />
            <span className="font-medium">{Math.round(pos.x)}%, {Math.round(pos.y)}%</span>
            {dirty && (
              <button
                onClick={(e) => { e.stopPropagation(); save(); }}
                onMouseDown={(e) => e.stopPropagation()}
                className="ml-1 bg-primary text-primary-foreground px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
              >
                <Save className="w-3 h-3" /> Enregistrer
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const Wrap = ({ banner, children }: { banner: AdBanner; children: React.ReactNode }) => {
  const { ref, isVisible } = useScrollAnimation();
  const pos = getPositionStyles(banner);
  const inner = (
    <div
      ref={ref as any}
      style={pos.inner}
      className={`${pos.widthClass} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
    >
      {children}
    </div>
  );
  const content = banner.link_url ? (
    <a
      href={banner.link_url}
      target={banner.link_url.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="block"
    >
      {inner}
    </a>
  ) : (
    inner
  );
  if (banner.free_enabled) return <FreeWrap banner={banner}>{content}</FreeWrap>;
  return <div className={`${pos.wrapper} my-6`}>{content}</div>;
};

const Media = ({ banner, className = "", forceFit }: { banner: AdBanner; className?: string; forceFit?: "cover" | "contain" }) => {
  if (!banner.media_url) return null;
  const fit = forceFit ?? banner.image_fit ?? "contain";
  const fitClass = fit === "contain" ? "object-contain bg-black/5" : "object-cover";
  if (banner.media_type === "video") {
    return (
      <video
        src={banner.media_url}
        autoPlay
        muted
        loop
        playsInline
        className={`${className} ${fitClass}`}
      />
    );
  }
  return (
    <img
      src={banner.media_url}
      alt={banner.title || "Visuel"}
      loading="lazy"
      className={`${className} ${fitClass}`}
    />
  );
};

// 1. Simple — image/vidéo + CTA (image affichée EN ENTIER par défaut)
const SimpleBanner = ({ banner }: { banner: AdBanner }) => (
  <div className="relative overflow-hidden rounded-2xl border border-border/50 shadow-lg group bg-muted/40">
    <Media banner={banner} className="w-full h-48 sm:h-56 md:h-64 transition-transform duration-700 group-hover:scale-105" />
    {(banner.title || banner.cta_label) && (
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-4 sm:p-6 pointer-events-none">
        {banner.title && <h3 className="text-white text-lg sm:text-xl font-bold drop-shadow">{banner.title}</h3>}
        {banner.cta_label && (
          <span className="mt-2 inline-block w-fit bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium pointer-events-auto">
            {banner.cta_label}
          </span>
        )}
      </div>
    )}
  </div>
);

// 2. Testimonial — style AlloPro : carte avec avatar + note + citation
const TestimonialBanner = ({ banner }: { banner: AdBanner }) => {
  const stars = Math.max(0, Math.min(5, banner.rating ?? 5));
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/20 shadow-lg bg-gradient-to-br from-primary/10 via-secondary/5 to-pink-500/10 p-5 sm:p-7">
      <div className="flex items-center gap-2 mb-4 text-primary text-sm font-semibold">
        <BadgeCheck className="w-5 h-5" />
        <span>Click&Gard&nbsp;·&nbsp;Témoignage pro</span>
      </div>
      <h3 className="text-foreground text-xl sm:text-2xl font-bold mb-5 font-display">
        {banner.title || "C'est vous qui en parlez le mieux"}
      </h3>
      <div className="bg-card/80 backdrop-blur rounded-xl p-4 sm:p-5 border border-border/50 shadow-sm">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            {banner.author_avatar_url ? (
              <img src={banner.author_avatar_url} alt={banner.author_name || ""} className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/30" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                {(banner.author_name || "?").slice(0, 1)}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-semibold truncate">{banner.author_name || "Anonyme"}</div>
              <div className="text-xs text-muted-foreground truncate">
                {[banner.author_location, banner.author_role].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <div className="flex shrink-0">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${i < stars ? "fill-primary text-primary" : "text-muted-foreground/30"}`} />
            ))}
          </div>
        </div>
        {banner.quote && (
          <p className="text-sm sm:text-base leading-relaxed text-foreground/90 italic">
            « {banner.quote} »
          </p>
        )}
      </div>
      {banner.media_url && (
        <div className="mt-4 rounded-xl overflow-hidden">
          <Media banner={banner} forceFit="cover" className="w-full h-32 sm:h-40" />
        </div>
      )}
    </div>
  );
};

// 3. Story — style "Histoires de Voisins"
const StoryBanner = ({ banner }: { banner: AdBanner }) => (
  <div className="relative overflow-hidden rounded-2xl border border-pink-300/30 shadow-lg bg-gradient-to-br from-pink-500/15 via-primary/10 to-purple-500/10">
    <div className="px-5 pt-5 sm:px-7 sm:pt-6 flex items-center gap-2 text-pink-500 text-sm font-semibold">
      <Heart className="w-4 h-4 fill-pink-500" />
      <span>Histoires de Click&Gard</span>
    </div>
    <div className="grid sm:grid-cols-[140px_1fr] gap-4 p-5 sm:p-7 items-center">
      <div className="relative">
        {banner.author_avatar_url ? (
          <img
            src={banner.author_avatar_url}
            alt={banner.author_name || ""}
            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover ring-4 ring-pink-500/40 mx-auto sm:mx-0"
          />
        ) : (
          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-pink-500/20 mx-auto sm:mx-0" />
        )}
      </div>
      <div className="text-center sm:text-left">
        <div className="text-pink-500 font-bold text-lg">{banner.author_name || "Une nounou"}</div>
        {banner.author_role && (
          <span className="inline-block text-xs bg-primary/15 text-primary px-2 py-0.5 rounded-full mt-1">
            {banner.author_role}
          </span>
        )}
        {banner.author_location && (
          <div className="text-pink-500/90 font-semibold text-sm mt-1">{banner.author_location}</div>
        )}
      </div>
    </div>
    <div className="px-5 sm:px-7 pb-3 text-center">
      <Quote className="w-6 h-6 text-pink-500/70 mx-auto mb-2" />
      <p className="text-base sm:text-lg font-bold text-foreground/90 italic max-w-xl mx-auto">
        {banner.quote || banner.title || "Une belle rencontre grâce à Click&Gard."}
      </p>
    </div>
    {banner.media_url && (
      <div className="px-5 sm:px-7">
        <div className="rounded-xl overflow-hidden">
          <Media banner={banner} forceFit="cover" className="w-full h-40 sm:h-52" />
        </div>
      </div>
    )}
    <div className="p-5 sm:p-7 pt-4 text-center">
      <span className="inline-block bg-pink-500 text-white px-5 py-2 rounded-full font-semibold text-sm shadow-md">
        {banner.cta_label || `Rencontrez ${banner.author_name?.split(" ")[0] || "cette nounou"}`}
      </span>
      {banner.story_excerpt && (
        <p className="mt-4 text-sm text-muted-foreground line-clamp-2 max-w-xl mx-auto">{banner.story_excerpt}</p>
      )}
    </div>
  </div>
);

// 4. Carousel — Thématiques cliquables
const CarouselBanner = ({ banner }: { banner: AdBanner }) => {
  const items: CarouselItem[] = Array.isArray(banner.carousel_items) ? banner.carousel_items : [];
  if (items.length === 0) return null;
  return (
    <div className="rounded-2xl border border-border/50 shadow-lg bg-card p-5 sm:p-6">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-lg sm:text-xl font-bold font-display">
          {banner.title || "Thématiques du moment"}
        </h3>
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </div>
      {banner.quote && <p className="text-sm text-muted-foreground mb-4">{banner.quote}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {items.slice(0, 3).map((it, i) => {
          const card = (
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden group cursor-pointer shadow-md">
              <img src={it.image_url} alt={it.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-0 p-3 flex flex-col justify-end text-white">
                <div className="font-bold text-sm sm:text-base leading-tight drop-shadow line-clamp-2">{it.title}</div>
                {(it.likes != null || it.shares != null) && (
                  <div className="flex items-center gap-3 mt-2 text-[11px] opacity-90">
                    {it.likes != null && (
                      <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{it.likes}</span>
                    )}
                    {it.shares != null && (
                      <span className="flex items-center gap-1"><Share2 className="w-3 h-3" />{it.shares}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
          return it.link_url ? (
            <a key={i} href={it.link_url} target={it.link_url.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
              {card}
            </a>
          ) : (
            <div key={i}>{card}</div>
          );
        })}
      </div>
      {banner.cta_label && (
        <div className="text-center mt-4">
          <span className="inline-block text-sm font-semibold text-primary border border-primary/40 px-4 py-2 rounded-full">
            {banner.cta_label}
          </span>
        </div>
      )}
    </div>
  );
};

const AdBannerSlot = ({ pageKey, index = 0, className = "" }: AdBannerSlotProps) => {
  const { banners } = useAdBanners(pageKey);
  if (!banners.length) return null;
  const banner = banners[index % banners.length];

  let body: React.ReactNode;
  switch (banner.format) {
    case "testimonial": body = <TestimonialBanner banner={banner} />; break;
    case "story":       body = <StoryBanner banner={banner} />; break;
    case "carousel":    body = <CarouselBanner banner={banner} />; break;
    default:            body = <SimpleBanner banner={banner} />;
  }

  return (
    <div className={className}>
      <Wrap banner={banner}>{body}</Wrap>
    </div>
  );
};

export default AdBannerSlot;
